import { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { Check, FileText, Gavel, Inbox, MailCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EASE, ScoreRing } from './primitives';
import { SKIP_ENTRANCE } from './motionPrefs';

/** Renders a score ring only once it scrolls into view so the fill animation is seen. */
export function InViewRing(props: Parameters<typeof ScoreRing>[0]) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const size = props.size ?? 56;
  return <span ref={ref} className="inline-flex shrink-0" style={{ width: size, height: size }}>{inView && <ScoreRing {...props} />}</span>;
}

const seen = { once: true } as const;

export function ApplicationVisual() {
  const reduced = useReducedMotion();
  const fields = [['Full name', 'Maya Chen'], ['Email', 'maya.chen@example.com'], ['Resume', 'maya-chen-resume.pdf']] as const;
  return (
    <div className="relative mx-auto w-full max-w-sm">
      <div className="rounded-2xl border border-bv-blue-line bg-white p-5 shadow-bv-window">
        <div className="flex items-center gap-2.5">
          <span className="bv-btn-gradient flex h-8 w-8 items-center justify-center rounded-lg text-[11px] font-bold text-white">YA</span>
          <div className="min-w-0">
            <p className="truncate text-xs text-bv-mute">youragency.com/careers</p>
            <p className="truncate text-sm font-semibold text-bv-ink">Apply · Senior Account Manager</p>
          </div>
        </div>
        <div className="mt-4 space-y-2.5">
          {fields.map(([label, value], i) => (
            <div key={label}>
              <p className="text-[11px] font-medium text-bv-mute">{label}</p>
              <div className="mt-1 flex h-9 items-center overflow-hidden rounded-lg border border-bv-blue-line px-3 text-[13px] text-bv-ink">
                {label === 'Resume' && <FileText className="mr-2 h-3.5 w-3.5 text-bv-blue" />}
                <motion.span className="whitespace-nowrap" initial={reduced || SKIP_ENTRANCE ? false : { clipPath: 'inset(0 100% 0 0)' }} whileInView={{ clipPath: 'inset(0 0% 0 0)' }} viewport={seen} transition={{ duration: 0.7, delay: 0.2 + i * 0.45, ease: 'easeOut' }}>{value}</motion.span>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1 text-[12px] text-bv-ink2">
            <motion.span initial={reduced || SKIP_ENTRANCE ? false : { scale: 0 }} whileInView={{ scale: 1 }} viewport={seen} transition={{ delay: 1.7, type: 'spring', stiffness: 400, damping: 18 }} className="flex h-4 w-4 items-center justify-center rounded bg-bv-blue text-white"><Check className="h-3 w-3" /></motion.span>
            Consent to recorded interview and data use
          </div>
        </div>
      </div>
      <motion.div initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: 16, scale: 0.95 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={seen} transition={{ delay: 2.1, duration: 0.5, ease: EASE }} className="absolute -bottom-5 -right-2 flex items-center gap-2 rounded-xl bg-bv-ink px-3 py-2 text-[13px] font-semibold text-white shadow-bv-deep sm:-right-6">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-bv-success"><Check className="h-3 w-3" /></span>Application received
      </motion.div>
    </div>
  );
}

export function ScreeningVisual() {
  const reduced = useReducedMotion();
  const criteria = [['Enterprise account renewals', true], ['Client team leadership', true], ['CRM pipeline reporting', true], ['Fluent German', false]] as const;
  return (
    <div className="mx-auto grid w-full max-w-lg gap-3 sm:grid-cols-[0.9fr_1.1fr]">
      <div className="relative overflow-hidden rounded-2xl border border-bv-blue-line bg-white p-4 shadow-bv-card">
        <div className="flex items-center gap-2 text-[12px] font-semibold text-bv-ink"><FileText className="h-4 w-4 text-bv-blue" />maya-chen-resume.pdf</div>
        <div className="mt-4 space-y-2.5">
          {[88, 72, 94, 58, 84, 66, 90, 52, 76].map((width, i) => (
            <div key={i} className={cn('h-2 rounded-full', i === 2 || i === 6 ? 'bg-bv-success/35' : 'bg-bv-blue-line')} style={{ width: `${width}%` }} />
          ))}
        </div>
        {!reduced && <motion.div aria-hidden className="absolute inset-x-0 h-12 border-b-2 border-bv-blue/40 bg-gradient-to-b from-transparent to-bv-blue/10" initial={{ top: '-15%' }} animate={{ top: '100%' }} transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }} />}
      </div>
      <div className="rounded-2xl border border-bv-blue-line bg-white p-4 shadow-bv-card">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-bv-mute">Role criteria</p>
          <InViewRing score={89} size={46} stroke={4} tone="success" />
        </div>
        <ul className="mt-3 space-y-2.5">
          {criteria.map(([criterion, found], i) => (
            <motion.li key={criterion} initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, x: 10 }} whileInView={{ opacity: 1, x: 0 }} viewport={seen} transition={{ delay: 0.3 + i * 0.3, ease: EASE }} className="flex items-center gap-2 text-[13px] text-bv-ink">
              <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded-full', found ? 'bg-bv-success text-white' : 'bg-bv-orange-soft text-bv-coral')}>
                {found ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <span className="h-0.5 w-2 rounded bg-current" />}
              </span>
              <span className={cn(!found && 'text-bv-ink2')}>{criterion}</span>
            </motion.li>
          ))}
        </ul>
        <p className="mt-3 rounded-lg bg-bv-blue-wash px-2.5 py-1.5 text-[11px] font-medium text-bv-ink2">Not evidenced stays visible to the recruiter</p>
      </div>
    </div>
  );
}

export function InviteVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="relative mx-auto w-full max-w-md">
      <div className="rounded-2xl border border-bv-blue-line bg-white p-4 shadow-bv-card">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-bv-mute"><Inbox className="h-3.5 w-3.5" />Example stage rule</p>
        <p className="mt-2 rounded-lg bg-bv-blue-wash px-3 py-2 font-mono text-[12px] text-bv-ink">IF resume match ≥ 80% → send AI interview</p>
      </div>
      <motion.div initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: 30, rotate: -2 }} whileInView={{ opacity: 1, y: 0, rotate: 0 }} viewport={seen} transition={{ delay: 0.4, duration: 0.7, ease: EASE }} className="relative z-10 -mt-1 ml-4 rounded-2xl border border-bv-blue-line bg-white p-4 shadow-bv-window sm:ml-8">
        <div className="flex items-center gap-3">
          <span className="bv-btn-gradient flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"><MailCheck className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-bv-ink">Your interview: Senior Account Manager</p>
            <p className="truncate text-xs text-bv-mute">To maya.chen@example.com</p>
          </div>
        </div>
        <p className="mt-3 text-[13px] leading-5 text-bv-ink2">Hi Maya — you’re invited to a short structured video interview. Complete it whenever suits you this week.</p>
        <span className="mt-3 inline-flex rounded-lg bg-bv-ink px-3 py-1.5 text-xs font-semibold text-white">Start interview</span>
      </motion.div>
      <motion.div initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, scale: 0.7 }} whileInView={{ opacity: 1, scale: 1 }} viewport={seen} transition={{ delay: 1.2, type: 'spring', stiffness: 260, damping: 18 }} className="absolute -right-1 top-[76px] z-20 flex items-center gap-1.5 rounded-full bg-bv-success px-3 py-1 text-xs font-semibold text-white shadow-lg">
        <Check className="h-3 w-3" strokeWidth={3} />Delivered · logged
      </motion.div>
    </div>
  );
}

export function InterviewVisual() {
  const reduced = useReducedMotion();
  const scores = [['Clarity', 94], ['Relevance', 91], ['Depth', 88]] as const;
  return (
    <div className="mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-bv-blue-line bg-white shadow-bv-window">
      <div className="relative aspect-[16/9] bg-[radial-gradient(circle_at_50%_30%,hsl(var(--navy-soft)),hsl(var(--ink)))]">
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-black/30 px-2 py-1 text-[11px] font-semibold text-white"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />REC · 01:24</span>
        <span className="absolute right-3 top-3 rounded-full bg-white/15 px-2 py-1 text-[11px] font-semibold text-white">Question 3 of 5</span>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 font-display text-xl font-semibold text-white ring-8 ring-white/5">AR</div>
        </div>
        <div className="absolute inset-x-6 bottom-3 flex h-9 items-end justify-center gap-[3px]" aria-hidden>
          {Array.from({ length: 32 }).map((_, i) => (
            <span key={i} className="bv-eq-bar bv-eq-bar-bottom w-[3px] rounded-full bg-bv-blue-bright/80" style={{ height: 8 + ((i * 37) % 26), animationDuration: `${0.8 + ((i * 13) % 7) / 10}s`, animationDelay: `${-((i * 7) % 10) / 10}s` }} />
          ))}
        </div>
      </div>
      <div className="p-4">
        <p className="text-[12px] font-medium text-bv-mute">Handling account risk</p>
        <p className="mt-1 text-sm font-semibold leading-5 text-bv-ink">“Tell us about a client relationship that was at risk. What did you do?”</p>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {scores.map(([label, value], i) => (
            <div key={label} className="rounded-lg bg-bv-blue-wash p-2">
              <p className="text-[11px] text-bv-mute">{label}</p>
              <div className="mt-1.5 h-1.5 rounded-full bg-white">
                <motion.div className="h-full rounded-full bg-bv-success" initial={reduced || SKIP_ENTRANCE ? false : { width: 0 }} whileInView={{ width: `${value}%` }} viewport={seen} transition={{ delay: 0.3 + i * 0.2, duration: 1, ease: EASE }} />
              </div>
              <p className="mt-1 text-xs font-semibold tabular-nums text-bv-ink">{value}%</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ReviewVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="relative mx-auto w-full max-w-md">
      <div className="rounded-2xl border border-bv-blue-line bg-white p-5 shadow-bv-window">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-bv-success-soft font-semibold text-bv-success">AR</span>
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-semibold text-bv-ink">Aisha Rahman</p>
            <p className="truncate text-[13px] text-bv-mute">Customer Success Lead</p>
          </div>
          <span className="ml-auto hidden rounded-full bg-bv-success-soft px-2.5 py-1 text-[11px] font-semibold text-bv-success sm:inline">Ready for review</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {[['Resume match', 91], ['Interview', 91]].map(([label, score]) => (
            <div key={label} className="flex items-center gap-3 rounded-xl bg-bv-blue-wash p-3">
              <InViewRing score={score as number} size={44} stroke={4} />
              <p className="text-[13px] font-semibold text-bv-ink">{label}</p>
            </div>
          ))}
        </div>
        <ul className="mt-4 space-y-1.5 text-[13px] text-bv-ink2">
          {['Owned enterprise onboarding', 'Improved renewal playbooks across two regions', '5 answers with AI assessments and full event history'].map(item => (
            <li key={item} className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bv-success" />{item}</li>
          ))}
        </ul>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <span className="bv-btn-gradient rounded-lg py-2 text-center text-[13px] font-semibold text-white">Shortlist</span>
          <span className="rounded-lg border border-bv-blue-line py-2 text-center text-[13px] font-semibold text-bv-ink">Share with client</span>
        </div>
      </div>
      <motion.div initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={seen} transition={{ delay: 0.9, duration: 0.5, ease: EASE }} className="absolute -bottom-5 -left-2 flex items-center gap-2 rounded-xl bg-bv-ink px-3 py-2 text-[13px] font-semibold text-white shadow-bv-deep sm:-left-5">
        <Gavel className="h-4 w-4 text-bv-blue-bright" />Your recruiter decides
      </motion.div>
    </div>
  );
}
